"use client"

import { type FormEvent, useEffect, useLayoutEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { EntityDetailSkeleton } from "@/components/layout/entity-detail-skeleton"
import { EmployerPostingPreferencesFields } from "@/components/employer/employer-posting-preferences-fields"
import { SettingsFormFooter } from "@/components/settings/settings-form-footer"
import { SettingsPageShell } from "@/components/settings/settings-page-shell"
import { SettingsPanel } from "@/components/settings/settings-panel"
import { useManagedOrganisationDetail } from "@/hooks/use-organisations"
import {
  organisationPostingPreferencesQueryKey,
  useOrganisationPostingPreferences,
} from "@/hooks/use-organisation-posting-preferences"
import { NOTIFICATIONS_QUERY_KEY, useNotificationPreferences } from "@/hooks/use-notifications"
import { useEmployerOrganisationOptional } from "@/context/employer-organisation-context"
import {
  employerOrganisationHomePath,
  managedEmployerOrgSlugStorageKey,
} from "@/constants"
import {
  EMPLOYER_ORGANISATION_SETTINGS_PREFERENCES_SEGMENT,
  getEmployerOrganisationSettingsSection,
} from "@/constants/employer-organisation-settings-nav"
import { navigateWithThreadPreservation } from "@/utils/thread-navigation"
import { CLIENT_LOCALE } from "@/lib/client-locale"
import { defaultEntityCountryCode } from "@/utils/entity-location-fields"
import {
  applicationReceivedEmailEnabled,
  applicationReceivedInAppEnabled,
  saveApplicationReceivedEmailPreference,
} from "@/lib/employer-application-notifications"
import {
  emptyEmployerPostingPreferences,
  employerPostingFieldsEqual,
  type EmployerPostingPreferences,
} from "@/lib/employer-posting-preferences"
import { saveOrganisationPostingPreferences } from "@/lib/employer-posting-preferences-api"

type EmployerOrganisationPreferencesSettingsPageProps = {
  slug?: string
}

export function EmployerOrganisationPreferencesSettingsPage({
  slug: slugProp,
}: EmployerOrganisationPreferencesSettingsPageProps) {
  const { slug: slugParam } = useParams<{ slug: string }>()
  const routeSlug = slugProp?.trim() || slugParam?.trim() || ""
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const ctx = useEmployerOrganisationOptional()
  const homePath = employerOrganisationHomePath()
  const defaultCountry = defaultEntityCountryCode(CLIENT_LOCALE)

  const preferencesSection = getEmployerOrganisationSettingsSection(
    EMPLOYER_ORGANISATION_SETTINGS_PREFERENCES_SEGMENT,
  )

  const { data: organisation, isLoading, error } = useManagedOrganisationDetail(routeSlug || undefined)
  const {
    data: loadedPrefs,
    isLoading: prefsLoading,
    isError: prefsError,
  } = useOrganisationPostingPreferences(routeSlug || undefined)
  const {
    preferences: notificationPrefs,
    isLoading: notifyLoading,
    isError: notifyError,
  } = useNotificationPreferences()

  const [prefs, setPrefs] = useState<EmployerPostingPreferences>(() =>
    emptyEmployerPostingPreferences(defaultCountry),
  )
  const [savedPrefs, setSavedPrefs] = useState<EmployerPostingPreferences>(() =>
    emptyEmployerPostingPreferences(defaultCountry),
  )
  const [notifyEmailEnabled, setNotifyEmailEnabled] = useState(true)
  const [savedNotifyEmailEnabled, setSavedNotifyEmailEnabled] = useState(true)
  const [notifyInAppEnabled, setNotifyInAppEnabled] = useState(true)
  const [prefsHydrated, setPrefsHydrated] = useState(false)
  const [notifyHydrated, setNotifyHydrated] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useLayoutEffect(() => {
    if (!routeSlug || !ctx?.enabled) return
    try {
      window.localStorage.setItem(managedEmployerOrgSlugStorageKey(), routeSlug)
    } catch {
      /* ignore */
    }
    ctx.setActiveOrganisationSlug(routeSlug)
  }, [routeSlug, ctx])

  useEffect(() => {
    setPrefsHydrated(false)
  }, [routeSlug])

  useEffect(() => {
    if (!loadedPrefs || prefsHydrated) return
    setPrefs(loadedPrefs)
    setSavedPrefs(loadedPrefs)
    setPrefsHydrated(true)
  }, [loadedPrefs, prefsHydrated])

  useEffect(() => {
    if (!notificationPrefs || notifyHydrated) return
    setNotifyEmailEnabled(applicationReceivedEmailEnabled(notificationPrefs))
    setSavedNotifyEmailEnabled(applicationReceivedEmailEnabled(notificationPrefs))
    setNotifyInAppEnabled(applicationReceivedInAppEnabled(notificationPrefs))
    setNotifyHydrated(true)
  }, [notificationPrefs, notifyHydrated])

  useEffect(() => {
    if (prefsError) {
      toast.error("Failed to load preferences")
    }
  }, [prefsError])

  useEffect(() => {
    if (notifyError) {
      toast.error("Failed to load notification settings")
    }
  }, [notifyError])

  const postingDirty = !employerPostingFieldsEqual(prefs, savedPrefs)
  const notifyDirty = notifyEmailEnabled !== savedNotifyEmailEnabled
  const isDirty = postingDirty || notifyDirty

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!routeSlug || !isDirty || isSaving) return

    setIsSaving(true)
    try {
      if (postingDirty) {
        const updated = await saveOrganisationPostingPreferences(routeSlug, prefs)
        setPrefs(updated)
        setSavedPrefs(updated)
        await queryClient.invalidateQueries({
          queryKey: organisationPostingPreferencesQueryKey(routeSlug),
        })
      }

      if (notifyDirty) {
        await saveApplicationReceivedEmailPreference({
          emailEnabled: notifyEmailEnabled,
          inAppEnabled: notifyInAppEnabled,
        })
        setSavedNotifyEmailEnabled(notifyEmailEnabled)
        await queryClient.invalidateQueries({
          queryKey: [...NOTIFICATIONS_QUERY_KEY, 'preferences'],
        })
      }

      toast.success("Preferences saved")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save preferences"
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const isPageLoading =
    (isLoading && !organisation) ||
    prefsLoading ||
    notifyLoading ||
    !prefsHydrated ||
    !notifyHydrated

  if (!routeSlug) {
    return (
      <SettingsPageShell title={preferencesSection?.label ?? "Preferences"}>
        <div className="text-center text-sm text-muted-foreground">Missing organisation.</div>
      </SettingsPageShell>
    )
  }

  if (isPageLoading) {
    return (
      <SettingsPageShell
        title={preferencesSection?.label ?? "Preferences"}
        description={preferencesSection?.description}
      >
        <EntityDetailSkeleton variant="settings" withPageContainer={false} ariaLabel="Loading preferences" />
      </SettingsPageShell>
    )
  }

  if (error || !organisation) {
    return (
      <SettingsPageShell title="Organisation not found">
        <div className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            This organisation does not exist or you do not have access to it.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigateWithThreadPreservation(navigate, homePath, searchParams)}
          >
            Back to organisation
          </Button>
        </div>
      </SettingsPageShell>
    )
  }

  return (
    <SettingsPageShell
      title={preferencesSection?.label ?? "Preferences"}
      description={preferencesSection?.description}
    >
      <SettingsPanel as="form" onSubmit={handleSubmit}>
        <EmployerPostingPreferencesFields
          layout="settings"
          prefs={prefs}
          onChange={setPrefs}
          notifyEmailEnabled={notifyEmailEnabled}
          onNotifyEmailChange={setNotifyEmailEnabled}
          disabled={isSaving}
        />

        <SettingsFormFooter>
          <Button type="submit" size="sm" disabled={isSaving || !isDirty}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                Saving...
              </>
            ) : (
              "Save changes"
            )}
          </Button>
        </SettingsFormFooter>
      </SettingsPanel>
    </SettingsPageShell>
  )
}
